import React from 'react';

import classNames from 'classnames';
import NumberFormat from 'react-number-format';

import { formatFigure, formatMoneyWithLocale } from 'Helpers/formatService';

import EditIcon from 'Assets/icons/edit.svg';

const moneyFigure = (value) => formatFigure(value, formatMoneyWithLocale, { precision: 3 });

/**
 * SponsorCommitmentLineTable
 *
 * The single pre-loaded commitment line of the Commit Sponsor screen — the sponsorship sibling of
 * RevenueCommitmentLineTable, sharing its blue header.
 *
 * Sponsorship is value-only, so the two editable figures are the committed CASH and committed VIK
 * amounts rather than a quantity and a price. Either may be zero — a VIK-only or cash-only deal is
 * ordinary here — and the combined value recalculates live. Replaces the old flow, which committed
 * the whole remaining balance with nothing editable at all.
 *
 * Props:
 *  - line:              { name, uncommittedCash, uncommittedVik }
 *  - cash, vik:         raw input strings, so a half-typed value survives editing
 *  - cashInvalid, vikInvalid:  negative, or beyond that component's balance — either way a
 *                              rejection the parent will not let through
 *  - committingValue:   cash + vik, computed by the parent so every surface agrees
 *  - onCashChange, onVikChange
 */
export default function SponsorCommitmentLineTable({
    line = {},
    cash = '',
    vik = '',
    cashInvalid = false,
    vikInvalid = false,
    committingValue = 0,
    onCashChange,
    onVikChange,
}) {
    // Written out in full rather than interpolated: Tailwind only emits classes it can see literally
    // in the source, so a computed `tw-text-${align}` would not be generated.
    const headerCell = (label, alignEnd = false) => (
        <th className={classNames('tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3', alignEnd ? 'tw-text-end' : 'tw-text-start')}>
            {label}
        </th>
    );

    // Two states, matching TicketingCommitmentLinesTable: red rejects the value, blue is ordinary.
    // The muted-red middle state went with IB-943 — over-commitment is now refused rather than
    // permitted-and-flagged, so nothing produces an accepted-but-negative value any more. That also
    // means aria-invalid is now correct on an over-commit, which it deliberately was not before.
    const numberInput = (value, { invalid = false }, ariaLabel, onChange) => (
        <div
            className={classNames(
                'tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-2 tw-py-1',
                invalid ? 'tw-border-red tw-bg-red-10' : 'tw-border-blue tw-bg-blue-10',
            )}
        >
            {/* NumberFormat, not a bare number input: a number input cannot carry thousands
                separators, which left the figures being typed as the only unseparated numbers on the
                screen. onValueChange hands the parent the UNformatted digits, so its arithmetic is
                unaffected. text-sm AND font-medium match the read-only cells beside it, per Dave's
                instruction that the committing inputs match the typography used elsewhere on the
                screen. The box, tint and pencil are the wireframe's, and stay. Both inputs come
                through here, so Cash and VIK are identical by construction. */}
            <NumberFormat
                value={value}
                thousandSeparator={true}
                decimalScale={2}
                allowNegative={false}
                inputMode="decimal"
                aria-label={ariaLabel}
                aria-invalid={invalid || undefined}
                onValueChange={({ value: raw }) => onChange && onChange(raw)}
                className="tw-w-28 tw-border-0 tw-bg-transparent tw-p-0 tw-text-end tw-text-sm tw-font-medium tw-tabular-nums tw-text-charcoal focus:tw-outline-none"
            />
            <EditIcon className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-text-blue" />
        </div>
    );

    // overflow-x-auto, not overflow-hidden: six columns (two of them number inputs) give this table a
    // real minimum width, and hidden clipped the Committing value column off the right edge below
    // ~1440px — the enclosing section's own overflow-x-auto never saw the overflow to scroll it.
    // Rounded corners still clip, because auto is not a visible overflow.
    return (
        <div className="tw-rounded-xl tw-border tw-border-charcoal-15 tw-overflow-x-auto tw-shadow-sm">
            <table className="tw-w-full inbud-table tw-text-sm">
                <thead>
                    <tr className="tw-bg-blue-25">
                        {headerCell($t('Sponsor'))}
                        {headerCell($t('Uncommitted Cash'), true)}
                        {headerCell($t('Uncommitted VIK'), true)}
                        {headerCell($t('Committing Cash'), true)}
                        {headerCell($t('Committing VIK'), true)}
                        {headerCell($t('Committing value'), true)}
                    </tr>
                </thead>
                <tbody>
                    <tr className="tw-border-b tw-border-charcoal-15">
                        <td className="tw-p-3 tw-text-charcoal tw-font-semibold">{line.name || '-'}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-medium">{moneyFigure(line.uncommittedCash)}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-medium">{moneyFigure(line.uncommittedVik)}</td>
                        <td className="tw-p-3 tw-text-end">{numberInput(cash, { invalid: cashInvalid }, $t('Committing Cash'), onCashChange)}</td>
                        <td className="tw-p-3 tw-text-end">{numberInput(vik, { invalid: vikInvalid }, $t('Committing VIK'), onVikChange)}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-bold">{moneyFigure(committingValue)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
